import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import BannerImage from "../../assets/anell360/studentlife.jpeg";
import { MdAdd, MdRemove } from 'react-icons/md';
import { BsWhatsapp } from "react-icons/bs";
import { Link } from 'react-router-dom';
import { DailyAnellGallery, LearnerWellbeingGallery, LeadershipVoiceGallery } from '../../utils/galleryAssets';


const AccordionItem = ({ title, content, isOpen, onClick, images }) => {
    return (
        <div className="border-b border-gray-200">
            <button
                className={`w-full flex items-center justify-between py-4 px-3 lg:px-5 transition-all duration-500 ${isOpen ? 'bg-[#021d53] text-white' : 'bg-white text-[#021d53] hover:bg-gray-50'}`}
                onClick={onClick}
            >
                <span className="text-xl font-black uppercase tracking-tight text-left">{title}</span>
                {isOpen ? <MdRemove className="text-3xl" /> : <MdAdd className="text-3xl" />}
            </button>
            <div 
                className={`overflow-hidden transition-all duration-700 ease-in-out ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}
            >
                <div className="p-6 lg:p-12 bg-white">
                    <p className="text-center text-gray-600 leading-relaxed mb-10 ">
                        {content}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {images && images.map((img, idx) => (
                            <div key={idx} className="h-64 lg:h-80 rounded overflow-hidden shadow-lg group">
                                <img 
                                    src={img} 
                                    alt={`${title} image ${idx + 1}`} 
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const StudentLife = () => {
    const location = useLocation();
    const [openIndex, setOpenIndex] = useState(-1);

    // useEffect(() => {
    //     window.scrollTo(0, 0);
    // }, [location]);

    const sections = [
        {
            title: "Daily Life at Anell",
            content: "Each school day is structured, purposeful, and engaging. From morning assemblies to closing reflections, learners experience a balanced routine that promotes discipline, focus, and enthusiasm for learning. Classrooms are interactive and supportive. The environment is clean, safe, and welcoming, creating the right atmosphere for growth.",
            images: DailyAnellGallery
        },
        {
            title: "Learner Wellbeing",
            content: "Every learner is known and supported. Academic guidance is paired with emotional and social care to ensure students feel valued and confident. Mentorship systems, open communication channels, and strong pastoral care structures help learners thrive both personally and academically.",
            images: LearnerWellbeingGallery
        },
        {
            title: "Leadership & Learner Voice",
            content: "Students are encouraged to lead and to be heard. Through prefectship roles, club leadership, and student-led initiatives, learners develop responsibility and confidence. They are given platforms to share ideas, contribute to school improvement, and grow as young leaders.",
            images: LeadershipVoiceGallery
        }
    ];

    return (
        <div className="font-sans text-gray-900 bg-white min-h-screen">
            

            {/* Hero Section */}
            <div className="relative h-[55vh] lg:h-[70vh] w-full flex items-center justify-center overflow-hidden bg-[#021d53]">
                <img 
                    src={BannerImage} 
                    alt="Student Life Banner" 
                    className="absolute inset-0 h-full w-full object-cover opacity-90 scale-105"
                />
                <div className="relative z-10 text-center px-5 max-w-5xl">
                    <h1 className="text-5xl md:text-8xl font-black text-white mb-6 uppercase tracking-tighter drop-shadow-2xl">
                        Student Life
                    </h1>
                    <div className="w-32 h-2 bg-[#ed3137] mx-auto mb-10"></div>
                    
                </div>
            
            </div>

            {/* Accordion Content Section */}
            <section className="py-20 lg:py-32 px-5 md:px-10 lg:px-20 max-w-[1440px] mx-auto">
                <div className="mb-16 text-center">
                    <h2 className="text-3xl md:text-5xl font-black text-[#021d53] mb-4">Discover the Anell Experience</h2>
                    <p className="text-gray-500 max-w-2xl mx-auto text-lg">Click on the sections below to explore how we foster a thriving environment for our learners.</p>
                </div>
                
                <div className="bg-white rounded shadow-2xl overflow-hidden border border-gray-100">
                    {sections.map((section, index) => (
                        <AccordionItem 
                            key={index}
                            title={section.title}
                            content={section.content}
                            images={section.images}
                            isOpen={openIndex === index}
                            onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                        />
                    ))}
                </div>
            </section>

            {/* Values / Highlights */}
            <section className="py-24 bg-gray-50">
                <div className="max-w-[1440px] mx-auto px-5 md:px-20 grid md:grid-cols-3 gap-12">
                    <div className="text-center space-y-6">
                        <div className="w-20 h-20 bg-white rounded shadow-xl flex items-center justify-center mx-auto text-4xl font-bold text-[#ed3137]">01</div>
                        <h4 className="text-2xl font-bold text-[#021d53]">Inclusive Culture</h4>
                        <p className="text-gray-600">Every learner finds their place and voice in our diverse community.</p>
                    </div>
                    <div className="text-center space-y-6">
                        <div className="w-20 h-20 bg-white rounded shadow-xl flex items-center justify-center mx-auto text-4xl font-bold text-[#ed3137]">02</div>
                        <h4 className="text-2xl font-bold text-[#021d53]">Holistic Growth</h4>
                        <p className="text-gray-600">Balancing academic rigor with social and emotional development.</p>
                    </div>
                    <div className="text-center space-y-6">
                        <div className="w-20 h-20 bg-white rounded shadow-xl flex items-center justify-center mx-auto text-4xl font-bold text-[#ed3137]">03</div>
                        <h4 className="text-2xl font-bold text-[#021d53]">Future Leaders</h4>
                        <p className="text-gray-600">Empowering students with the confidence to lead and innovate.</p>
                    </div>
                </div>
            </section>

            <Footer />
             
        </div>
    );
};

export default StudentLife;
